import { useMemo } from "react";
import {
  Payment,
  type ActivityReservation,
  type PaymentBreakdown,
} from "@b2bportal/rbc-activities-api";
import { useI18nContext } from "@hopper-b2b/i18n";
import { getAgeBandLabel } from "@tenant/activities";

export const useCheckoutBreakdown = (
  reservation: ActivityReservation,
  paymentBreakdown?: PaymentBreakdown
) => {
  const { t, formatFiatCurrency, formatRewardsCurrency } = useI18nContext();

  return useMemo(() => {
    const bookingSession = reservation.bookingSession;
    const priceBreakdownItems = bookingSession?.priceBreakdown ?? [];

    const lineItems = priceBreakdownItems.map((lineItem) => {
      const ageBandLabel = getAgeBandLabel(lineItem.specificAgeBand, t);
      const pricePerPerson = formatFiatCurrency(
        lineItem.specificAgeBand.pricePerTraveler.fiat
      );
      const totalForAgeBand = formatFiatCurrency(
        lineItem.totalPriceForAgeBand.fiat
      );
      const count = lineItem.specificAgeBand.numberOfTravelers;

      return {
        label: t("core-activities.shopPage.priceBreakdown.lineItem", {
          pricePerPerson,
          count,
          ageBandLabel,
        }),
        value: totalForAgeBand,
      };
    });

    const bookingFeeFiat = bookingSession?.bookingFee?.fiat;
    const bookingFee =
      bookingFeeFiat != null && bookingFeeFiat.value > 0
        ? formatFiatCurrency(bookingFeeFiat)
        : undefined;

    const total = bookingSession?.totalSellAtPriceWithFees
      ? formatFiatCurrency(bookingSession.totalSellAtPriceWithFees.fiat)
      : "";

    const paymentLineItems = paymentBreakdown?.paymentLineItems;
    const cardPayment = paymentLineItems?.find(
      ({ type }) => type === Payment.Card
    );
    const rewardsPayment = paymentLineItems?.find(
      ({ type }) => type === Payment.RbcAvionPoints
    );
    const creditPayment = paymentLineItems?.find(
      ({ type }) => type === Payment.TravelWalletCredit
    );

    const amountPaid = cardPayment?.value?.paymentAmount
      ? formatFiatCurrency({
          value: cardPayment.value.paymentAmount.amount,
          currencyCode: cardPayment.value.paymentAmount.currency,
        })
      : formatFiatCurrency({
          value: 0,
          currencyCode:
            bookingSession?.totalSellAtPriceWithFees?.fiat?.currencyCode ??
            "CAD",
        });
    const paymentMethod = cardPayment?.value?.cardNumberDisplay ?? undefined;

    const numberOfFlexiblePoints =
      rewardsPayment?.value?.pointsRedemptionBreakdown
        ?.numberOfFlexiblePoints ?? 0;

    const travelCreditAmount = creditPayment?.value?.paymentAmount?.amount ?? 0;
    const travelCreditsApplied =
      creditPayment && travelCreditAmount > 0
        ? {
            label: t("trips.travelCreditsApplied"),
            value: `- ${formatFiatCurrency({
              value: travelCreditAmount,
              currencyCode:
                creditPayment.value?.paymentAmount?.currency ?? "CAD",
            })}`,
          }
        : undefined;

    let redeemedPoints: { label: string; value: string } | undefined;
    if (numberOfFlexiblePoints > 0) {
      const pointsFormatted = formatRewardsCurrency(
        {
          value: numberOfFlexiblePoints,
          currency: "Points",
        },
        { minimumFractionDigits: 0, maximumFractionDigits: 0 }
      );
      const flexFiat =
        rewardsPayment?.value?.pointsRedemptionBreakdown
          ?.flexiblePointsCashEquivalent;
      const fiatValue = flexFiat?.value
        ? `- ${formatFiatCurrency({
            value: flexFiat.value,
            currencyCode: flexFiat.currencyCode ?? "CAD",
          })}`
        : "";
      redeemedPoints = {
        label: t("trips.redeemedPoints", { points: pointsFormatted }),
        value: fiatValue,
      };
    }

    return {
      lineItems,
      bookingFee,
      total,
      travelCreditsApplied,
      amountPaid,
      paymentMethod,
      redeemedPoints,
    };
  }, [
    reservation,
    paymentBreakdown,
    formatFiatCurrency,
    formatRewardsCurrency,
    t,
  ]);
};
